import { ContactSelect, CurrencySelect, DateInputLocalized } from '@components'

import { Flex, Input, Spacing, Tag } from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import type { Contact } from '@features/contacts/types/contact'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../../../../enums/queryKeys'
import { useForm } from '../../../../../../hooks'
import { getReviewEntryDataFormSchema } from '../../../../types/getReviewEntryDataFormSchema'
import type { ReviewEntryDataFormValues } from '../../../../types/getReviewEntryDataFormSchema'
import type { ReviewEntryLine } from '../../../../types/reviewEntryLine'
import { getReviewEntryDraft } from '../../../../utils/reviewEntryDraftStorage'
import { ReviewEntryLineItems } from './elements/ReviewEntryLineItems'
import { useReviewEntryDataFormDefaultValues } from './hooks/useReviewEntryDataFormDefaultValues'
import { useReviewEntryDraftAutosave } from './hooks/useReviewEntryDraftAutosave'
import * as Styled from './styles'

export const REVIEW_ENTRY_DATA_FORM_ID = 'review-entry-data-form'

interface ReviewEntryDataFormProps {
  currencyId?: string
  entryId?: string
  entryLines: ReviewEntryLine[]
  invoiceNumber?: string
  isNewSupplier?: boolean
  isPrefillLoading?: boolean
  isReadOnly?: boolean
  isSubmitting?: boolean
  onFormReadyChange?: (isReady: boolean) => void
  onSubmit?: (values: ReviewEntryDataFormValues) => void
  receiptDate?: string
  sourceFingerprint?: string
  supplierContact?: Partial<Contact>
  supplierId?: string
  supplierName?: string
}

export const ReviewEntryDataForm = ({
  currencyId,
  entryId,
  entryLines,
  invoiceNumber,
  isNewSupplier = false,
  isPrefillLoading = false,
  isReadOnly = false,
  isSubmitting = false,
  onFormReadyChange,
  onSubmit,
  receiptDate,
  sourceFingerprint,
  supplierContact,
  supplierId,
  supplierName,
}: ReviewEntryDataFormProps): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const queryClient = useQueryClient()
  const organizationId = organization?.id
  const initializedSourceKeyRef = useRef<string | undefined>()
  const lastDefinedFingerprintRef = useRef(sourceFingerprint)
  const [isFormReady, setIsFormReady] = useState(false)

  if (sourceFingerprint !== undefined) {
    lastDefinedFingerprintRef.current = sourceFingerprint
  }

  const stableSourceFingerprint = sourceFingerprint ?? lastDefinedFingerprintRef.current
  const formSourceKey = `${entryId ?? ''}::${stableSourceFingerprint ?? ''}`
  const { accounts, defaultValues, isDataPending } = useReviewEntryDataFormDefaultValues({
    currencyId,
    entryLines,
    invoiceNumber,
    receiptDate,
    supplierContact,
    supplierId,
    supplierName,
  })

  const { Form, FormItem, reset, watch } = useForm({
    validationSchema: getReviewEntryDataFormSchema(t),
    defaultValues,
    onSubmit,
  })

  const persistWatch = useCallback(
    (callback: (values: unknown) => void) => watch((values) => callback(values)),
    [watch],
  )
  const supplierValue = watch('supplier')
  const isUnresolvedNewSupplier = isNewSupplier && !supplierValue?.id

  useReviewEntryDraftAutosave({
    entryId,
    isFormReady,
    isReadOnly,
    isSubmitting,
    organizationId,
    sourceFingerprint: stableSourceFingerprint,
    watch: persistWatch,
  })

  const handleSupplierCreated = useCallback(() => {
    queryClient.invalidateQueries(QueryKeys.Contacts)
    queryClient.invalidateQueries(QueryKeys.ContactsAndContactPersons)
  }, [queryClient])

  useLayoutEffect(() => {
    if (initializedSourceKeyRef.current === undefined || initializedSourceKeyRef.current === formSourceKey) {
      return
    }

    setIsFormReady(false)
    onFormReadyChange?.(false)
  }, [formSourceKey, onFormReadyChange])

  useEffect(() => {
    if (isDataPending || isPrefillLoading || initializedSourceKeyRef.current === formSourceKey) {
      return
    }

    initializedSourceKeyRef.current = formSourceKey
    const draft =
      !isReadOnly && entryId && organizationId
        ? getReviewEntryDraft({ entryId, organizationId, sourceFingerprint: stableSourceFingerprint })
        : undefined
    reset(draft ? { ...defaultValues, ...draft } : defaultValues)
    setIsFormReady(true)
    onFormReadyChange?.(true)
  }, [
    defaultValues,
    entryId,
    formSourceKey,
    isDataPending,
    isPrefillLoading,
    isReadOnly,
    onFormReadyChange,
    organizationId,
    reset,
    stableSourceFingerprint,
  ])

  return (
    <Form id={REVIEW_ENTRY_DATA_FORM_ID}>
      <Styled.ReviewEntryDataFormFieldList>
        <Styled.ReviewEntryDataFormFieldsGroup>
          <Styled.ReviewEntryDataFormInvoiceNumberWrapper>
            <FormItem
              label={t('review_entries.detail.field.invoice_number.label')}
              name="invoiceNumber"
              render={({ field: { error, ...field } }) => <Input {...field} error={error} disabled={isReadOnly} />}
            />
          </Styled.ReviewEntryDataFormInvoiceNumberWrapper>
          <FormItem
            extra={isUnresolvedNewSupplier ? t('review_entries.detail.field.supplier.new_supplier_caption') : undefined}
            label={t('review_entries.detail.field.supplier.label')}
            name="supplier"
            render={({ field: { onChange, value, id, error } }) => {
              const contactSelect = (
                <ContactSelect
                  allowClear
                  allowCreateNew
                  disabled={!isFormReady || isReadOnly}
                  error={error}
                  id={id}
                  inputValue={isReadOnly && supplierName ? supplierName : value?.name}
                  isSupplier
                  onCreateNew={handleSupplierCreated}
                  onSelect={(selectedId, contact) => onChange(contact ?? null)}
                  selectedId={value?.id}
                  selectedValue={value ?? undefined}
                  withSearch
                />
              )

              if (!isUnresolvedNewSupplier) {
                return contactSelect
              }

              return (
                <Flex alignItems="center" gap={Spacing.S8}>
                  <Styled.ReviewEntryDataFormSupplierSelect>{contactSelect}</Styled.ReviewEntryDataFormSupplierSelect>
                  <Tag color="purple" size="md" type="subtle">
                    {t('review_entries.detail.field.supplier.new_supplier_tag')}
                  </Tag>
                </Flex>
              )
            }}
          />
          <Styled.ReviewEntryDataFormDatesRow>
            <Styled.ReviewEntryDataFormDateGroup>
              <FormItem
                label={t('review_entries.detail.field.receipt_date.label')}
                name="receiptDate"
                render={({ field: { onChange, value, id, name, error } }) => (
                  <DateInputLocalized
                    disabled={isReadOnly}
                    id={id}
                    name={name}
                    error={error}
                    onChange={onChange}
                    value={value ?? null}
                  />
                )}
              />
              <FormItem
                label={t('review_entries.detail.field.due_date.label')}
                name="dueDate"
                render={({ field: { onChange, value, id, name } }) => (
                  <DateInputLocalized
                    disabled={isReadOnly}
                    id={id}
                    name={name}
                    onChange={onChange}
                    value={value ?? null}
                  />
                )}
              />
            </Styled.ReviewEntryDataFormDateGroup>
            <FormItem
              label={t('review_entries.detail.field.currency.label')}
              name="currencyId"
              render={({ field: { onChange, value, id, error } }) => (
                <CurrencySelect
                  disabled={isReadOnly}
                  error={error}
                  id={id}
                  onSelect={(selectedId) => onChange(selectedId)}
                  selectedId={value ?? undefined}
                />
              )}
            />
          </Styled.ReviewEntryDataFormDatesRow>
        </Styled.ReviewEntryDataFormFieldsGroup>
        <ReviewEntryLineItems accounts={accounts} isFormReady={isFormReady} isReadOnly={isReadOnly} />
      </Styled.ReviewEntryDataFormFieldList>
    </Form>
  )
}
